import { useEffect, useRef, useState } from "react";

import Button from "../components/layout/Button";
import LoadingSpinner from "../components/layout/LoadingSpinner";
import FormError from "../components/salesDocuments/FormError";
import {
  BulkStatusResponse,
  extractBulkErrorMessage,
  getBulkStatus,
} from "../services/crmBulkUpload.service";
import {
  fileToBase64,
  InvoicePipelineLogEntry,
  InvoicePipelineResponse,
  submitInvoicePipeline,
} from "../services/invoicePipeline.service";

import {
  ACCEPTED_FILE_EXTENSIONS,
  IN_PROGRESS_STATUSES,
  LOG_LEVEL_COLORS,
  MAX_FILE_SIZE_BYTES,
  POLL_INTERVAL_MS,
  POLL_TIMEOUT_MS,
  supplierOptions,
} from "./InvoicePipeline.constants";

interface StatusHistoryEntry {
  status: string;
  updatedAt: string | null;
  isFailure: boolean;
}

interface ZohoImportDetail {
  added_count?: number;
  total_count?: number;
  skipped_count?: number;
}

interface ZohoBulkCallback {
  resource?: { importDetails?: ZohoImportDetail[] }[];
}

const isTerminalStatus = (status: string | null): boolean =>
  !!status && !IN_PROGRESS_STATUSES.includes(status);

const formatBytes = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(1)}MB`;

const getLogColor = (level: string): string =>
  LOG_LEVEL_COLORS[level.toUpperCase()] ?? LOG_LEVEL_COLORS.DEBUG;

const OutputLogs = ({
  logs,
  showDebug,
}: {
  logs: InvoicePipelineLogEntry[];
  showDebug: boolean;
}) => (
  <div className="bulk-upload__json" style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
    {logs
      .filter((log) => showDebug || log.level.toUpperCase() !== "DEBUG")
      .map((log, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} style={{ color: getLogColor(log.level), whiteSpace: "pre-wrap" }}>
          {log.level.toUpperCase()} : {log.msg}
        </div>
      ))}
  </div>
);

const InvoicePipeline = () => {
  const [supplier, setSupplier] = useState("");
  const [supplierInvoiceRef, setSupplierInvoiceRef] = useState("");
  const [invoiceReceivedDate, setInvoiceReceivedDate] = useState("");
  const [password, setPassword] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const [supplierError, setSupplierError] = useState(false);
  const [supplierInvoiceRefError, setSupplierInvoiceRefError] = useState(false);
  const [invoiceReceivedDateError, setInvoiceReceivedDateError] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [result, setResult] = useState<InvoicePipelineResponse | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);
  const [statusResult, setStatusResult] = useState<BulkStatusResponse | null>(null);
  const [statusHistory, setStatusHistory] = useState<StatusHistoryEntry[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const [showDebugLogs, setShowDebugLogs] = useState(false);

  const pollStartRef = useRef<number>(0);

  useEffect(() => {
    if (!jobId || !isPolling) return undefined;

    pollStartRef.current = Date.now();

    const poll = async () => {
      try {
        const status = await getBulkStatus(jobId);
        setStatusResult(status);
        setPollError(null);

        const isTerminal = isTerminalStatus(status.status);
        const label = status.status ?? "UNKNOWN";
        setStatusHistory((prev) => {
          if (prev.length > 0 && prev[prev.length - 1].status === label) return prev;
          return [
            ...prev,
            {
              status: label,
              updatedAt: status.updated_at,
              isFailure: isTerminal && label !== "COMPLETED",
            },
          ];
        });

        if (isTerminal) {
          setIsPolling(false);
          return;
        }
      } catch (error) {
        setPollError(extractBulkErrorMessage(error));
      }

      if (Date.now() - pollStartRef.current >= POLL_TIMEOUT_MS) {
        setIsPolling(false);
        setPollError("Stopped polling after 10 minutes. Check back on this job later.");
      }
    };

    void poll();
    const interval = setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
    };
  }, [jobId, isPolling]);

  const resetForm = () => {
    setSupplier("");
    setSupplierInvoiceRef("");
    setInvoiceReceivedDate("");
    setPassword("");
    setFile(null);
  };

  const resetJobState = () => {
    setJobId(null);
    setStatusResult(null);
    setStatusHistory([]);
    setIsPolling(false);
    setPollError(null);
  };

  const validate = (): boolean => {
    let valid = true;

    const supplierMissing = supplier.trim() === "";
    setSupplierError(supplierMissing);
    if (supplierMissing) valid = false;

    const refMissing = supplierInvoiceRef.trim() === "";
    setSupplierInvoiceRefError(refMissing);
    if (refMissing) valid = false;

    const dateMissing = invoiceReceivedDate.trim() === "";
    setInvoiceReceivedDateError(dateMissing);
    if (dateMissing) valid = false;

    if (!file) {
      setFileError("Please select an invoice file.");
      valid = false;
    } else if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError(
        `File is too large (${formatBytes(file.size)}). Please select a file under ${formatBytes(MAX_FILE_SIZE_BYTES)}.`,
      );
      valid = false;
    } else {
      setFileError(null);
    }

    return valid;
  };

  const handleSubmit = async () => {
    setRequestError(null);
    setResult(null);
    resetJobState();

    if (!validate() || !file) return;

    setIsSubmitting(true);
    try {
      const fileContentBase64 = await fileToBase64(file);
      const response = await submitInvoicePipeline({
        supplier,
        supplier_invoice_ref: supplierInvoiceRef.trim(),
        invoice_received_date: invoiceReceivedDate,
        filename: file.name,
        file_content_base64: fileContentBase64,
        ...(password.trim() !== "" && { password: password.trim() }),
      });

      setResult(response);
      if (response.mode === "bulk") {
        setJobId(response.job_id);
        setIsPolling(true);
      }
      resetForm();
    } catch (error) {
      setRequestError(extractBulkErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const zohoRows =
    result?.mode === "small_batch"
      ? (result.zoho_response.data as { status?: string }[] | undefined)
      : undefined;
  const zohoRowFailures =
    zohoRows?.filter((row) => row.status && row.status !== "success").length ?? 0;
  const zohoRowSuccesses = (zohoRows?.length ?? 0) - zohoRowFailures;

  const importSummary = (() => {
    const callback = statusResult?.callback as ZohoBulkCallback | null | undefined;
    const details = callback?.resource?.[0]?.importDetails?.[0];
    if (!details) return null;
    return {
      added: details.added_count ?? 0,
      total: details.total_count ?? 0,
      skipped: details.skipped_count ?? 0,
    };
  })();

  return (
    <div>
      <h1>Invoice Pipeline</h1>

      <div className="meter-search-container">
        <div>
          <h4>Instructions for use</h4>
          Upload a supplier invoice file to normalise it and write the resulting payment records
          into Zoho CRM.
          <ol>
            <li>
              Choose the supplier that produced this invoice from the supplier dropdown.{" "}
              <strong>Selecting the wrong supplier will produce a silent failure!</strong>.
            </li>
            <li>
              Enter the supplier invoice reference and the date the invoice was received.{" "}
              <span style={{ color: "red" }}>
                <strong>
                  IMPORTANT! The supplier invoice reference must be unique to the file - 1
                  reference, 1 file.
                </strong>
              </span>{" "}
              Exceptions:
              <ul>
                <li>
                  <u>Example 1:</u> Total Energy sends one file with two references, <i>12345</i>{" "}
                  and <i>12346</i>. Either:
                  <ol type="i">
                    <li>
                      Upload one file with one supplier invoice reference <i>12345_12346</i>.
                    </li>
                    <li>
                      Or, upload two files where the rows corresponding to the other reference are{" "}
                      <strong>DELETED</strong>.
                    </li>
                  </ol>
                  <ul>
                    <li>
                      <span style={{ color: "red" }}>
                        <strong>
                          Do NOT hide rows, the pipeline will detect them and write them to Zoho.
                        </strong>
                      </span>
                    </li>
                  </ul>
                </li>
                <li>
                  <u>Example 2:</u> Engie sends three files but one invoice reference.{" "}
                  <span style={{ color: "red" }}>
                    <strong>Do NOT upload three files with the same reference.</strong>
                  </span>{" "}
                  Use, for example, <i>AINV-1234_upf</i>, <i>AINV-1234_rec</i>, <i>AINV-1234_pos</i>{" "}
                  for the upfront, reconciliation, and post contract statements respectively.
                </li>
              </ul>
            </li>
            <li>
              Select the invoice file (xlsx, xls, csv or pdf). If it&apos;s a password-protected
              Office file, enter its password.
            </li>
            <li>
              Click &apos;Submit&apos;. Small invoices are written to Zoho immediately; larger ones
              are staged as a background job that this page will track for you.
            </li>
            <li>
              <u>Confirm the number of rows ingested and written to Zoho.</u> Check warnings, and
              investigate rejected entries by looking at the original invoice file.
            </li>
          </ol>
        </div>

        <fieldset className="docs-form__section">
          <div className="docs-form__customer-field">
            <div className="docs-form__inputs">
              <label htmlFor="invoice-pipeline-supplier">Supplier</label>
              <select
                id="invoice-pipeline-supplier"
                value={supplier}
                onChange={(event) => {
                  setSupplier(event.target.value);
                  setSupplierError(false);
                  setRequestError(null);
                }}
              >
                <option value="" disabled>
                  Select a supplier
                </option>
                {supplierOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            {supplierError && (
              <div className="docs-form__error-messages">
                <FormError text="Please select a supplier." />
              </div>
            )}
          </div>

          <div className="docs-form__customer-field">
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <div className="docs-form__inputs" style={{ flex: 1 }}>
                <label htmlFor="invoice-pipeline-ref">Supplier invoice reference</label>
                <input
                  id="invoice-pipeline-ref"
                  type="text"
                  value={supplierInvoiceRef}
                  onChange={(event) => {
                    setSupplierInvoiceRef(event.target.value);
                    setSupplierInvoiceRefError(false);
                    setRequestError(null);
                  }}
                  placeholder="e.g. GEAB-1284"
                />
              </div>
              <div className="docs-form__inputs" style={{ flex: 1 }}>
                <label htmlFor="invoice-pipeline-date">Invoice received date</label>
                <input
                  id="invoice-pipeline-date"
                  type="date"
                  value={invoiceReceivedDate}
                  onChange={(event) => {
                    setInvoiceReceivedDate(event.target.value);
                    setInvoiceReceivedDateError(false);
                    setRequestError(null);
                  }}
                />
              </div>
            </div>
            {supplierInvoiceRefError && (
              <div className="docs-form__error-messages">
                <FormError text="Please enter the supplier invoice reference." />
              </div>
            )}
            {invoiceReceivedDateError && (
              <div className="docs-form__error-messages">
                <FormError text="Please enter the invoice received date." />
              </div>
            )}
          </div>

          <div className="docs-form__customer-field">
            <div className="docs-form__inputs">
              <label htmlFor="invoice-pipeline-password">
                Password <small>(optional, only for protected files)</small>
              </label>
              <input
                id="invoice-pipeline-password"
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setRequestError(null);
                }}
              />
            </div>
          </div>

          <div className="docs-form__customer-field">
            <div className="docs-form__inputs">
              <label htmlFor="invoice-pipeline-file">Invoice file</label>
              <input
                id="invoice-pipeline-file"
                type="file"
                accept={ACCEPTED_FILE_EXTENSIONS}
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  setFileError(null);
                  setRequestError(null);
                }}
              />
            </div>
            {fileError && (
              <div className="docs-form__error-messages">
                <FormError text={fileError} />
              </div>
            )}
          </div>

          {requestError && (
            <div className="docs-form__error-messages">
              <FormError text={requestError} />
            </div>
          )}

          <div className="meter-details-footer">
            {isSubmitting ? (
              <LoadingSpinner message="Uploading..." size="button" />
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                Submit
              </Button>
            )}
          </div>
        </fieldset>

        {result && (
          <fieldset className="docs-form__section">
            <div className="docs-form__customer-field">
              <p>
                <strong>Processing log</strong>
              </p>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontWeight: "normal",
                  marginTop: "0.5rem",
                }}
              >
                <input
                  type="checkbox"
                  checked={showDebugLogs}
                  onChange={(event) => {
                    setShowDebugLogs(event.target.checked);
                  }}
                />
                Show debug logs
              </label>
              <div style={{ marginTop: "0.5rem" }}>
                <OutputLogs logs={result.output_logs} showDebug={showDebugLogs} />
              </div>
            </div>
          </fieldset>
        )}

        {result?.mode === "small_batch" && (
          <fieldset className="docs-form__section">
            <div className="docs-form__customer-field">
              <label>
                <strong>
                  Upload{" "}
                  {zohoRowFailures > 0
                    ? zohoRowSuccesses > 0
                      ? "partially complete"
                      : "failed"
                    : "complete"}
                </strong>
              </label>
              <p>
                <span style={{ color: zohoRowFailures > 0 ? "#c0341d" : "#1a7f37" }}>
                  {zohoRowSuccesses} of {result.row_count} row{result.row_count === 1 ? "" : "s"}{" "}
                  written to Zoho successfully.
                </span>
              </p>
              {zohoRowFailures > 0 && (
                <div className="docs-form__error-messages">
                  <FormError
                    text={`${String(zohoRowFailures)} row${zohoRowFailures === 1 ? "" : "s"} failed to write.`}
                  />
                </div>
              )}
            </div>
          </fieldset>
        )}

        {jobId && (
          <fieldset className="docs-form__section">
            <div className="docs-form__customer-field">
              <p>
                <strong>Job status</strong>
              </p>
              <p>
                This invoice had too many rows to write synchronously, so it was staged as a
                background job.
              </p>
              <p>
                Job ID: <code>{jobId}</code>
              </p>

              <div style={{ marginTop: "0.75rem" }}>
                {statusHistory.map((entry, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <p key={index} style={{ margin: "0.25rem 0" }}>
                    <span
                      style={{
                        color: entry.isFailure ? "#c0341d" : "#1a7f37",
                        marginRight: "0.5rem",
                      }}
                    >
                      {entry.isFailure ? "✗" : "✓"}
                    </span>
                    <strong>{entry.status}</strong>
                    {entry.updatedAt && ` — ${entry.updatedAt}`}
                  </p>
                ))}
                {isPolling && <p style={{ margin: "0.25rem 0" }}>Checking for updates...</p>}
              </div>

              {pollError && (
                <div className="docs-form__error-messages">
                  <FormError text={pollError} />
                </div>
              )}

              {!isPolling && statusResult && !isTerminalStatus(statusResult.status) && (
                <Button
                  type="button"
                  onClick={() => {
                    setIsPolling(true);
                  }}
                >
                  Check status again
                </Button>
              )}

              {importSummary && (
                <p style={{ marginTop: "0.75rem" }}>
                  {importSummary.added} of {importSummary.total} row
                  {importSummary.total === 1 ? "" : "s"} written to Zoho successfully.
                  {importSummary.skipped > 0 && ` (${String(importSummary.skipped)} skipped)`}
                </p>
              )}
            </div>
          </fieldset>
        )}
      </div>
    </div>
  );
};

export default InvoicePipeline;
